import type { PrebuildReviewState } from "@/lib/api/databook";

export interface BuilderStageNavigationItem {
  key: string;
  label: string;
}

export const DATABOOK_BUILDER_STAGES = [
  { key: "upload", label: "Upload" },
  { key: "coverage", label: "Update RFI" },
  { key: "ents", label: "Legal entities" },
  { key: "basis", label: "Basis of preparation" },
  { key: "book", label: "Databook" },
  { key: "qna", label: "Q&A" },
  { key: "report", label: "Report" },
] as const satisfies readonly BuilderStageNavigationItem[];

export const CONNECTED_DATABOOK_BUILDER_STAGES = [
  { key: "upload", label: "Upload" },
  { key: "coverage", label: "Update RFI" },
  { key: "ents", label: "Legal entities" },
  { key: "basis", label: "Basis of preparation" },
  { key: "book", label: "Databook" },
  { key: "qna", label: "Q&A" },
  { key: "report", label: "Report" },
] as const satisfies readonly BuilderStageNavigationItem[];

export type ConnectedDatabookBuilderStage =
  (typeof CONNECTED_DATABOOK_BUILDER_STAGES)[number]["key"];

export function connectedDatabookBuilderStages(
  showCoverage: boolean,
): readonly BuilderStageNavigationItem[] {
  return CONNECTED_DATABOOK_BUILDER_STAGES.filter(
    (stage) => showCoverage || stage.key !== "coverage",
  );
}

export function shouldShowQuestionsStage(
  state: PrebuildReviewState | undefined,
  batchId: string | null | undefined,
  questionCount: number,
): boolean {
  return Boolean(
    batchId
    && questionCount > 0
    && state
    && ["questions_required", "running", "failed", "ready"].includes(state),
  );
}

export function postAnalysisBuilderStage(
  recommendNotBuild: boolean,
): ConnectedDatabookBuilderStage {
  if (recommendNotBuild) return "coverage";
  return "ents";
}

type BuilderStageStorage = Pick<Storage, "getItem" | "setItem">;

const CONNECTED_STAGE_KEYS = new Set<string>(
  CONNECTED_DATABOOK_BUILDER_STAGES.map((stage) => stage.key),
);

function storageKey(projectId: string): string {
  return `dilly.databookBuilder.lastStage.${projectId}`;
}

export function loadLastBuilderStage(
  projectId: string | undefined,
  storage?: BuilderStageStorage,
): ConnectedDatabookBuilderStage | null {
  if (!projectId) return null;
  try {
    const savedStage = (storage ?? window.localStorage).getItem(
      storageKey(projectId),
    );
    return savedStage && CONNECTED_STAGE_KEYS.has(savedStage)
      ? savedStage as ConnectedDatabookBuilderStage
      : null;
  } catch {
    return null;
  }
}

export function saveLastBuilderStage(
  projectId: string | undefined,
  stage: string,
  storage?: BuilderStageStorage,
): void {
  if (!projectId || !CONNECTED_STAGE_KEYS.has(stage)) return;
  try {
    (storage ?? window.localStorage).setItem(storageKey(projectId), stage);
  } catch {
    // Browsers may disable or limit local storage.
  }
}
